import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Heading,
  SimpleGrid,
  Skeleton,
  Stack,
  SkeletonText,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";

import Fiat from "views/user/dashboard/components/Fiat";
import KycInfo from "views/user/profile/components/KycInfo";
import VirtualAccountInfo from "views/user/dashboard/components/VirtualAccountInfo";
import Transactions from "views/user/transactions/components/Transactions";
import Card from "components/card/Card.js";

export default function UserDashboard() {
  const search = useLocation().search;

  const userState = useSelector((state) => state?.user?.value);
  const token = userState.hasOwnProperty("token") ? userState.token : null;

  const tx_ref = new URLSearchParams(search).get("tx_ref");
  const transaction_id = new URLSearchParams(search).get("transaction_id");

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [userLoading, setUserLoading] = useState(true);
  const [verifyDepositLoading, setVerifyDepositLoading] = useState(true);
  const [walletLoading, setWalletLoading] = useState(true);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [user, setUser] = useState(null);

  const [pageCount, setPageCount] = useState(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const [from, setFrom] = useState(0);
  const [limit, setLimit] = useState(10);
  const [orderBy, setOrderBy] = useState("createdAt");

  const toast = useToast();

  const textColorSecondary = useColorModeValue("gray.800", "white");

  const verifyDeposit = async () => {
    try {
      setVerifyDepositLoading(true);
      const { data: response } = await axios.post(
        SERVER_URL + "/wallet/verifyDeposit",
        { tx_ref: tx_ref, transaction_id: transaction_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        // redirect to payment link
        toast({
          title: response.message,
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        getUser();
        getWallet();
        getTransactions();
      }
      setVerifyDepositLoading(false);
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
    setVerifyDepositLoading(false);
  };

  const createVirtualAccount = async () => {
    try {
      setWalletLoading(true);
      const { data: response } = await axios.get(
        SERVER_URL + "/wallet/createVirtualAccount",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        toast({
          title: "Virtual account has been issued successfuly",
          status: "success",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        getWallet();
        getUser();
      }
      setWalletLoading(false);
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      setWalletLoading(false);
    }
  };

  const getUser = async () => {
    try {
      const { data: response } = await axios.get(SERVER_URL + "/user/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }
      if (response.success) {
        setUser(response.data);
        setUserLoading(false);
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  };

  const getWallet = async () => {
    try {
      const { data: response } = await axios.get(SERVER_URL + "/wallet/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        setWalletLoading(false);
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        setWalletLoading(false);
        return;
      }
      if (response.success) {
        setWallet(response.data);
        setWalletLoading(false);
      }
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  };

  const getTransactions = async () => {
    try {
      const { data: response } = await axios.get(
        SERVER_URL +
          "/transaction?from=" +
          from +
          "&limit=" +
          limit +
          "&orderBy=" +
          orderBy +
          "&sortOrder=" +
          sortOrder,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.error) {
        toast({
          title: response.error || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (!response.success) {
        toast({
          title: response.message || "An error occured",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
        return;
      }
      if (response.success) {
        setPageCount(response.data.count / limit);
        setTransactions(response.data.transactions);
      }
      setTransactionLoading(false);
    } catch (error) {
      toast({
        title: error.message || "An error occured",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (transaction_id !== undefined && transaction_id !== null) {
      verifyDeposit();
    } else {
      getUser();
      getWallet();
      getTransactions();
    }
  }, []);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid
        columns={{ base: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
        gap="10px"
        mb="50px"
      >
        {walletLoading ? <LoadingFiat /> : <Fiat wallet={wallet} user={user} />}
        {userLoading || walletLoading ? (
          <LoadingKyc />
        ) : user.kycStatus !== "completed" ? (
          <KycInfo user={user} getUser={getUser} />
        ) : (
          <VirtualAccountInfo
            user={user}
            wallet={wallet}
            createVirtualAccount={createVirtualAccount}
          />
        )}
      </SimpleGrid>
      <SimpleGrid columns={2} mt={2}>
        <Box>
          <Heading
            color={textColorSecondary}
            fontSize={{ base: "md", sm: "md", md: "lg", lg: "xl" }}
            ml={{ base: "5px", sm: "5px" }}
            fontFamily="ManropeSemiBold"
          >
            Transactions
          </Heading>
        </Box>
        <Box textAlign="right">
          <Link color={"blue.400"} to="/user/transactions">
            All Transactions
          </Link>
        </Box>
      </SimpleGrid>
      {transactionLoading ? (
        <LoadingTransaction />
      ) : (
        <Transactions transactions={transactions} />
      )}
    </Box>
  );
}

const LoadingFiat = () => {
  return (
    <Card align="center" p="20px">
      <Stack>
        <Skeleton height="30px" />
        <SkeletonText mt="5" noOfLines={5} spacing="4" />
        <Skeleton height="30px" />
      </Stack>
    </Card>
  );
};

const LoadingKyc = () => {
  return (
    <Card align="center" p="20px">
      <Stack>
        <Skeleton height="30px" />
        <SkeletonText mt="5" noOfLines={5} spacing="4" />
        <Skeleton height="30px" />
      </Stack>
    </Card>
  );
};

const LoadingTransaction = () => {
  return (
    <>
      <Card align="center" p="5px">
        <Stack>
          <Skeleton height="5px" />
          <SkeletonText mt="2" noOfLines={3} spacing="3" />
          <Skeleton height="5px" />
        </Stack>
      </Card>

      <Card align="center" p="5px" mt="5">
        <Stack>
          <Skeleton height="5px" />
          <SkeletonText mt="2" noOfLines={3} spacing="3" />
          <Skeleton height="5px" />
        </Stack>
      </Card>
    </>
  );
};
